import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Switch } from '../../ui/switch';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
import { 
  Lock, CheckCircle, XCircle, Loader2, ExternalLink, 
  CreditCard, DollarSign, FileText, Clock, Save, RefreshCw,
  Copy, Check, HelpCircle, ChevronDown, ChevronUp
} from 'lucide-react';
import PasswordStrengthMeter from '../../security/PasswordStrengthMeter';
import SecurityDashboard from '../../security/SecurityDashboard';
import GoogleOAuthSettings from '../GoogleOAuthSettings';
import GoogleMapsSettings from '../GoogleMapsSettings';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Setup Guide Component
const SetupGuide = ({ title, steps, links }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="mt-4 border rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50">
      <button 
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-3 flex items-center justify-between text-left hover:bg-blue-100/50 rounded-t-lg transition-colors"
      >
        <div className="flex items-center gap-2">
          <HelpCircle className="h-5 w-5 text-blue-600" />
          <span className="font-medium text-blue-800">{title}</span>
        </div>
        {isExpanded ? <ChevronUp className="h-4 w-4 text-blue-600" /> : <ChevronDown className="h-4 w-4 text-blue-600" />}
      </button>
      
      {isExpanded && (
        <div className="p-4 pt-0 space-y-4">
          {/* Quick Links */}
          <div className="flex flex-wrap gap-2 pb-3 border-b border-blue-200">
            {links.map((link, idx) => (
              <a 
                key={idx}
                href={link.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-blue-200 rounded-full text-sm text-blue-700 hover:bg-blue-50 hover:border-blue-300 transition-colors"
              >
                <ExternalLink className="h-3 w-3" />
                {link.label}
              </a>
            ))}
          </div>
          
          {/* Step by Step Guide */}
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-800 text-sm">Step-by-Step Setup:</h4>
            <ol className="space-y-2">
              {steps.map((step, idx) => (
                <li key={idx} className="flex gap-3 text-sm">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
                    {idx + 1}
                  </span>
                  <span className="text-gray-700 pt-0.5">{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
};

export default function PlatformSettings() {
  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Billing settings state
  const [billingLoading, setBillingLoading] = useState(true);
  const [saving, setSaving] = useState({});
  
  // Gateway configurations
  const [stripeConfig, setStripeConfig] = useState({
    publishable_key: '',
    secret_key: '',
    webhook_secret: '',
    test_mode: true,
    enabled: false
  });
  
  const [paypalConfig, setPaypalConfig] = useState({
    client_id: '',
    client_secret: '',
    sandbox_mode: true,
    enabled: false
  });
  
  const [squareConfig, setSquareConfig] = useState({
    application_id: '',
    access_token: '',
    location_id: '',
    sandbox_mode: true,
    enabled: false
  });
  
  const [trialSettings, setTrialSettings] = useState({
    default_duration_days: 30,
    grace_period_days: 3,
    auto_start_trial: true
  });
  
  const [paymentSettings, setPaymentSettings] = useState({
    currency: 'USD',
    tax_rate: 0,
    failed_payment_retries: 3
  });
  
  const [invoiceSettings, setInvoiceSettings] = useState({
    invoice_prefix: 'INV-',
    payment_terms_days: 30,
    auto_generate_invoices: true
  });

  // Domain settings state
  const [domainSettings, setDomainSettings] = useState({
    default_domain: '',
    patient_portal_path: '/patient',
    allow_clinic_override: true
  });
  const [domainLoading, setDomainLoading] = useState(true);
  const [domainSaving, setDomainSaving] = useState(false);

  // Load billing settings
  useEffect(() => {
    loadBillingSettings();
    loadDomainSettings();
  }, []);

  const loadDomainSettings = async () => {
    try {
      setDomainLoading(true);
      const response = await axios.get(`${API_URL}/api/platform-settings/domain`);
      if (response.data) {
        setDomainSettings(response.data);
      }
    } catch (error) {
      console.error('Error loading domain settings:', error);
      // Use default if not configured
    } finally {
      setDomainLoading(false);
    }
  };

  const saveDomainSettings = async () => {
    try {
      setDomainSaving(true);
      await axios.post(`${API_URL}/api/platform-settings/domain`, domainSettings);
      toast.success('Domain settings saved successfully');
    } catch (error) {
      console.error('Error saving domain settings:', error);
      toast.error('Failed to save domain settings');
    } finally {
      setDomainSaving(false);
    }
  };

  const loadBillingSettings = async () => {
    try {
      setBillingLoading(true);
      const response = await axios.get(`${API_URL}/api/billing-settings`);
      const data = response.data;
      
      setStripeConfig(data.stripe || stripeConfig);
      setPaypalConfig(data.paypal || paypalConfig);
      setSquareConfig(data.square || squareConfig);
      setTrialSettings(data.trial || trialSettings);
      setPaymentSettings(data.payment || paymentSettings);
      setInvoiceSettings(data.invoice || invoiceSettings);
    } catch (error) {
      console.error('Error loading billing settings:', error);
      toast.error('Failed to load billing settings');
    } finally {
      setBillingLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordMessage(null);

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'Please fill in all fields' });
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setPasswordMessage({ type: 'error', text: 'Password must be at least 8 characters long' });
      return;
    }

    try {
      setPasswordLoading(true);
      
      await axios.put(`${API_URL}/api/auth/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });

      setPasswordMessage({ type: 'success', text: 'Password changed successfully!' });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      setPasswordMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to change password'
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  // Save handlers for billing settings
  const saveStripeConfig = async () => {
    try {
      setSaving({ ...saving, stripe: true });
      await axios.put(`${API_URL}/api/billing-settings/stripe`, stripeConfig);
      toast.success('Stripe configuration saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Stripe configuration');
    } finally {
      setSaving({ ...saving, stripe: false });
    }
  };

  const savePaypalConfig = async () => {
    try {
      setSaving({ ...saving, paypal: true });
      await axios.put(`${API_URL}/api/billing-settings/paypal`, paypalConfig);
      toast.success('PayPal configuration saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save PayPal configuration');
    } finally {
      setSaving({ ...saving, paypal: false });
    }
  };

  const saveSquareConfig = async () => {
    try {
      setSaving({ ...saving, square: true });
      await axios.put(`${API_URL}/api/billing-settings/square`, squareConfig);
      toast.success('Square configuration saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Square configuration');
    } finally {
      setSaving({ ...saving, square: false });
    }
  };

  const saveTrialSettings = async () => {
    try {
      setSaving({ ...saving, trial: true });
      await axios.put(`${API_URL}/api/billing-settings/trial`, trialSettings);
      toast.success('Trial settings saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save trial settings');
    } finally {
      setSaving({ ...saving, trial: false });
    }
  };

  const savePaymentSettings = async () => {
    try {
      setSaving({ ...saving, payment: true });
      await axios.put(`${API_URL}/api/billing-settings/payment`, paymentSettings);
      toast.success('Payment settings saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save payment settings');
    } finally {
      setSaving({ ...saving, payment: false });
    }
  };

  const saveInvoiceSettings = async () => {
    try {
      setSaving({ ...saving, invoice: true });
      await axios.put(`${API_URL}/api/billing-settings/invoice`, invoiceSettings);
      toast.success('Invoice settings saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save invoice settings');
    } finally {
      setSaving({ ...saving, invoice: false });
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Platform Settings</h1>
        <p className="text-gray-600">Configure platform-wide settings and preferences</p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Settings</CardTitle>
          <CardDescription>Platform configuration options</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="profile" className="w-full">
            <TabsList className="grid w-full grid-cols-8">
              <TabsTrigger value="profile">Profile</TabsTrigger>
              <TabsTrigger value="security">Security</TabsTrigger>
              <TabsTrigger value="domain">Domain</TabsTrigger>
              <TabsTrigger value="integrations">Integrations</TabsTrigger>
              <TabsTrigger value="billing">Billing</TabsTrigger>
              <TabsTrigger value="general">General</TabsTrigger>
              <TabsTrigger value="email">Email</TabsTrigger>
              <TabsTrigger value="system">System</TabsTrigger>
            </TabsList>

            <TabsContent value="profile" className="space-y-6 mt-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Profile & Security</h3>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Lock className="h-5 w-5" />
                      Change Password
                    </CardTitle>
                    <CardDescription>Update your account password</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {passwordMessage && (
                      <Alert variant={passwordMessage.type === 'error' ? 'destructive' : 'default'} className="mb-4">
                        {passwordMessage.type === 'success' ? (
                          <CheckCircle className="h-4 w-4" />
                        ) : (
                          <XCircle className="h-4 w-4" />
                        )}
                        <AlertDescription>{passwordMessage.text}</AlertDescription>
                      </Alert>
                    )}

                    <form onSubmit={handlePasswordChange} className="space-y-4">
                      <div>
                        <Label htmlFor="currentPassword">Current Password *</Label>
                        <Input
                          id="currentPassword"
                          type="password"
                          value={passwordForm.currentPassword}
                          onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                          placeholder="Enter current password"
                          disabled={passwordLoading}
                        />
                      </div>

                      <div>
                        <Label htmlFor="newPassword">New Password *</Label>
                        <Input
                          id="newPassword"
                          type="password"
                          value={passwordForm.newPassword}
                          onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                          placeholder="Enter new password (min 12 characters)"
                          disabled={passwordLoading}
                        />
                        <PasswordStrengthMeter password={passwordForm.newPassword} />
                      </div>

                      <div>
                        <Label htmlFor="confirmPassword">Confirm New Password *</Label>
                        <Input
                          id="confirmPassword"
                          type="password"
                          value={passwordForm.confirmPassword}
                          onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                          placeholder="Confirm new password"
                          disabled={passwordLoading}
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={passwordLoading}
                        className="bg-teal-600 hover:bg-teal-700"
                      >
                        {passwordLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Changing Password...
                          </>
                        ) : (
                          <>
                            <Lock className="mr-2 h-4 w-4" />
                            Change Password
                          </>
                        )}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="security" className="space-y-6 mt-6">
              <SecurityDashboard />
            </TabsContent>

            {/* Domain Settings Tab */}
            <TabsContent value="domain" className="space-y-6 mt-6">
              <div>
                <h3 className="text-lg font-semibold mb-4">Domain Configuration</h3>
                <p className="text-gray-600 mb-6">Configure the default domain for patient portal URLs and email links.</p>
                
                {domainLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
                  </div>
                ) : (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <ExternalLink className="h-5 w-5" />
                        Patient Portal Domain
                      </CardTitle>
                      <CardDescription>
                        This domain will be used for patient login URLs in welcome emails and notifications.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Default Domain */}
                      <div className="space-y-2">
                        <Label htmlFor="defaultDomain">Default Domain *</Label>
                        <Input
                          id="defaultDomain"
                          type="url"
                          placeholder="https://mymedmember.com"
                          value={domainSettings.default_domain}
                          onChange={(e) => setDomainSettings({ ...domainSettings, default_domain: e.target.value })}
                          className="font-mono"
                        />
                        <p className="text-xs text-gray-500">
                          The base URL for patient portal links (e.g., https://mymedmember.com)
                        </p>
                      </div>

                      {/* Patient Portal Path */}
                      <div className="space-y-2">
                        <Label htmlFor="portalPath">Patient Portal Path</Label>
                        <Input
                          id="portalPath"
                          type="text"
                          placeholder="/patient"
                          value={domainSettings.patient_portal_path}
                          onChange={(e) => setDomainSettings({ ...domainSettings, patient_portal_path: e.target.value })}
                          className="font-mono"
                        />
                        <p className="text-xs text-gray-500">
                          URL path for patient login (default: /patient)
                        </p>
                      </div>

                      {/* Allow Clinic Override */}
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <Label htmlFor="allowOverride" className="font-medium">Allow Clinic Override</Label>
                          <p className="text-sm text-gray-500">
                            Allow individual clinics to use their own custom domain
                          </p>
                        </div>
                        <Switch
                          id="allowOverride"
                          checked={domainSettings.allow_clinic_override}
                          onCheckedChange={(checked) => setDomainSettings({ ...domainSettings, allow_clinic_override: checked })}
                        />
                      </div>

                      {/* URL Preview */}
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <Label className="text-blue-800 font-medium">URL Preview</Label>
                        <p className="font-mono text-sm text-blue-700 mt-2">
                          {domainSettings.default_domain || 'https://example.com'}{domainSettings.patient_portal_path || '/patient'}/{'{clinic-subdomain}'}
                        </p>
                        <p className="text-xs text-blue-600 mt-2">
                          Example: {domainSettings.default_domain || 'https://example.com'}{domainSettings.patient_portal_path || '/patient'}/rosasmedical
                        </p>
                      </div>

                      {/* Save Button */}
                      <div className="flex justify-end pt-4">
                        <Button 
                          onClick={saveDomainSettings}
                          disabled={domainSaving}
                          className="bg-teal-600 hover:bg-teal-700"
                        >
                          {domainSaving ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="h-4 w-4 mr-2" />
                              Save Domain Settings
                            </>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            <TabsContent value="integrations" className="space-y-6 mt-6">
              <GoogleOAuthSettings />
              <GoogleMapsSettings />
            </TabsContent>

            <TabsContent value="billing" className="space-y-6 mt-6">
              {billingLoading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
                </div>
              ) : (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Billing & Payment Gateway Configuration</h3>
                    <Button variant="outline" size="sm" onClick={loadBillingSettings}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh
                    </Button>
                  </div>
                  
                  {/* Stripe Configuration */}
                  <div className="space-y-4 mb-6 p-4 border rounded-lg bg-purple-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                          <CreditCard className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold">Stripe</h4>
                          <p className="text-xs text-gray-500">Industry-leading payment processor</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {stripeConfig.enabled ? (
                          <Badge className="bg-green-100 text-green-800">Enabled</Badge>
                        ) : (
                          <Badge variant="outline">Disabled</Badge>
                        )}
                        <Badge variant={stripeConfig.test_mode ? "secondary" : "default"}>
                          {stripeConfig.test_mode ? "Test Mode" : "Live Mode"}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg border border-purple-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Publishable Key</Label>
                          <Input 
                            type="text" 
                            placeholder="pk_test_..." 
                            className="font-mono text-sm"
                            value={stripeConfig.publishable_key}
                            onChange={(e) => setStripeConfig({ ...stripeConfig, publishable_key: e.target.value })}
                          />
                          <p className="text-xs text-gray-500 mt-1">Public key for frontend checkout</p>
                        </div>
                        <div>
                          <Label>Secret Key</Label>
                          <Input 
                            type="password" 
                            placeholder="sk_test_..." 
                            className="font-mono text-sm"
                            value={stripeConfig.secret_key}
                            onChange={(e) => setStripeConfig({ ...stripeConfig, secret_key: e.target.value })}
                          />
                          <p className="text-xs text-gray-500 mt-1">Secret key for backend API calls</p>
                        </div>
                        <div>
                          <Label>Webhook Secret</Label>
                          <Input 
                            type="password" 
                            placeholder="whsec_..." 
                            className="font-mono text-sm"
                            value={stripeConfig.webhook_secret}
                            onChange={(e) => setStripeConfig({ ...stripeConfig, webhook_secret: e.target.value })}
                          />
                          <p className="text-xs text-gray-500 mt-1">For payment event webhooks</p>
                        </div>
                        <div className="space-y-3 pt-4">
                          <div className="flex items-center gap-2">
                            <Switch 
                              id="stripe-test-mode" 
                              checked={stripeConfig.test_mode}
                              onCheckedChange={(checked) => setStripeConfig({ ...stripeConfig, test_mode: checked })}
                            />
                            <Label htmlFor="stripe-test-mode">Test Mode</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch 
                              id="stripe-enabled" 
                              checked={stripeConfig.enabled}
                              onCheckedChange={(checked) => setStripeConfig({ ...stripeConfig, enabled: checked })}
                            />
                            <Label htmlFor="stripe-enabled">Enable Stripe</Label>
                          </div>
                        </div>
                      </div>
                      
                      <SetupGuide
                        title="Stripe Setup Guide for Developers"
                        links={[
                          { label: "Stripe Dashboard", url: "https://dashboard.stripe.com/" },
                          { label: "API Keys", url: "https://dashboard.stripe.com/apikeys" },
                          { label: "Webhooks", url: "https://dashboard.stripe.com/webhooks" },
                          { label: "Documentation", url: "https://stripe.com/docs" }
                        ]}
                        steps={[
                          "Go to Stripe Dashboard → Sign in or create account",
                          "Navigate to Developers → API Keys",
                          "Copy 'Publishable key' (starts with pk_test_ or pk_live_)",
                          "Click 'Reveal' and copy 'Secret key' (starts with sk_test_ or sk_live_)",
                          "For webhooks: Go to Developers → Webhooks → Add endpoint",
                          "Set endpoint URL to: [your-domain]/api/webhooks/stripe",
                          "Select events: payment_intent.succeeded, payment_intent.failed, customer.subscription.*",
                          "Copy 'Signing secret' (starts with whsec_) for webhook verification"
                        ]}
                      />
                      
                      <div className="flex justify-end mt-4">
                        <Button 
                          onClick={saveStripeConfig}
                          disabled={saving.stripe}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          {saving.stripe ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                          ) : (
                            <><Save className="h-4 w-4 mr-2" /> Save Stripe Configuration</>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* PayPal Configuration */}
                  <div className="space-y-4 mb-6 p-4 border rounded-lg bg-yellow-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center">
                          <DollarSign className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold">PayPal</h4>
                          <p className="text-xs text-gray-500">Trusted worldwide payment solution</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {paypalConfig.enabled ? (
                          <Badge className="bg-green-100 text-green-800">Enabled</Badge>
                        ) : (
                          <Badge variant="outline">Disabled</Badge>
                        )}
                        <Badge variant={paypalConfig.sandbox_mode ? "secondary" : "default"}>
                          {paypalConfig.sandbox_mode ? "Sandbox" : "Live"}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg border border-yellow-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Client ID</Label>
                          <Input 
                            type="text" 
                            placeholder="AYSq3RDGsmBLJE..." 
                            className="font-mono text-sm"
                            value={paypalConfig.client_id}
                            onChange={(e) => setPaypalConfig({ ...paypalConfig, client_id: e.target.value })}
                          />
                          <p className="text-xs text-gray-500 mt-1">From PayPal Developer Dashboard</p>
                        </div>
                        <div>
                          <Label>Client Secret</Label>
                          <Input 
                            type="password" 
                            placeholder="EKP6X7..." 
                            className="font-mono text-sm"
                            value={paypalConfig.client_secret}
                            onChange={(e) => setPaypalConfig({ ...paypalConfig, client_secret: e.target.value })}
                          />
                          <p className="text-xs text-gray-500 mt-1">Secret for backend authentication</p>
                        </div>
                        <div className="flex items-center gap-2 pt-4">
                          <Switch 
                            id="paypal-sandbox" 
                            checked={paypalConfig.sandbox_mode}
                            onCheckedChange={(checked) => setPaypalConfig({ ...paypalConfig, sandbox_mode: checked })}
                          />
                          <Label htmlFor="paypal-sandbox">Sandbox Mode</Label>
                        </div>
                        <div className="flex items-center gap-2 pt-4">
                          <Switch 
                            id="paypal-enabled" 
                            checked={paypalConfig.enabled}
                            onCheckedChange={(checked) => setPaypalConfig({ ...paypalConfig, enabled: checked })}
                          />
                          <Label htmlFor="paypal-enabled">Enable PayPal</Label>
                        </div>
                      </div>
                      
                      <SetupGuide
                        title="PayPal Setup Guide for Developers"
                        links={[
                          { label: "Developer Dashboard", url: "https://developer.paypal.com/dashboard/" },
                          { label: "My Apps", url: "https://developer.paypal.com/dashboard/applications/live" },
                          { label: "Sandbox Accounts", url: "https://developer.paypal.com/dashboard/accounts" },
                          { label: "REST API Docs", url: "https://developer.paypal.com/docs/api/overview/" }
                        ]}
                        steps={[
                          "Go to developer.paypal.com → Log in with PayPal account",
                          "Click 'Dashboard' → 'My Apps & Credentials'",
                          "Select 'Sandbox' or 'Live' tab based on your needs",
                          "Click 'Create App' → Enter app name → Click 'Create App'",
                          "Copy 'Client ID' from the app details page",
                          "Click 'Show' under Secret → Copy 'Secret' key",
                          "For sandbox testing: Create sandbox accounts under 'Sandbox Accounts'",
                          "Important: Switch to 'Live' and create new credentials before going to production"
                        ]}
                      />
                      
                      <div className="flex justify-end mt-4">
                        <Button 
                          onClick={savePaypalConfig}
                          disabled={saving.paypal}
                          className="bg-yellow-600 hover:bg-yellow-700"
                        >
                          {saving.paypal ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                          ) : (
                            <><Save className="h-4 w-4 mr-2" /> Save PayPal Configuration</>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Square Configuration */}
                  <div className="space-y-4 mb-6 p-4 border rounded-lg bg-green-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                          <CreditCard className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold">Square</h4>
                          <p className="text-xs text-gray-500">Perfect for clinics with POS systems</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {squareConfig.enabled ? (
                          <Badge className="bg-green-100 text-green-800">Enabled</Badge>
                        ) : (
                          <Badge variant="outline">Disabled</Badge>
                        )}
                        <Badge variant={squareConfig.sandbox_mode ? "secondary" : "default"}>
                          {squareConfig.sandbox_mode ? "Sandbox" : "Live"}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg border border-green-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Application ID</Label>
                          <Input 
                            type="text" 
                            placeholder="sandbox-sq0idb-..." 
                            className="font-mono text-sm"
                            value={squareConfig.application_id}
                            onChange={(e) => setSquareConfig({ ...squareConfig, application_id: e.target.value })}
                          />
                          <p className="text-xs text-gray-500 mt-1">From Square Developer Dashboard</p>
                        </div>
                        <div>
                          <Label>Access Token</Label>
                          <Input 
                            type="password" 
                            placeholder="EAAAEOuLw..." 
                            className="font-mono text-sm"
                            value={squareConfig.access_token}
                            onChange={(e) => setSquareConfig({ ...squareConfig, access_token: e.target.value })}
                          />
                          <p className="text-xs text-gray-500 mt-1">Personal access token for API</p>
                        </div>
                        <div>
                          <Label>Location ID</Label>
                          <Input 
                            type="text" 
                            placeholder="LAAA..." 
                            className="font-mono text-sm"
                            value={squareConfig.location_id}
                            onChange={(e) => setSquareConfig({ ...squareConfig, location_id: e.target.value })}
                          />
                          <p className="text-xs text-gray-500 mt-1">Your business location ID</p>
                        </div>
                        <div className="space-y-3 pt-4">
                          <div className="flex items-center gap-2">
                            <Switch 
                              id="square-sandbox" 
                              checked={squareConfig.sandbox_mode}
                              onCheckedChange={(checked) => setSquareConfig({ ...squareConfig, sandbox_mode: checked })}
                            />
                            <Label htmlFor="square-sandbox">Sandbox Mode</Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch 
                              id="square-enabled" 
                              checked={squareConfig.enabled}
                              onCheckedChange={(checked) => setSquareConfig({ ...squareConfig, enabled: checked })}
                            />
                            <Label htmlFor="square-enabled">Enable Square</Label>
                          </div>
                        </div>
                      </div>
                      
                      <SetupGuide
                        title="Square Setup Guide for Developers"
                        links={[
                          { label: "Developer Dashboard", url: "https://developer.squareup.com/apps" },
                          { label: "Sandbox Test", url: "https://developer.squareup.com/console/sandbox/test" },
                          { label: "Locations API", url: "https://developer.squareup.com/explorer/square/locations-api" },
                          { label: "API Reference", url: "https://developer.squareup.com/reference/square" }
                        ]}
                        steps={[
                          "Go to developer.squareup.com → Sign in or create Square account",
                          "Click 'Applications' → '+' to create new application",
                          "Enter application name → Click 'Save'",
                          "In the app page, find 'Credentials' section",
                          "Copy 'Sandbox Application ID' (for testing) or 'Production Application ID'",
                          "Under 'Access Token', copy the token (or generate new one)",
                          "To get Location ID: Go to 'Locations' tab or use API Explorer",
                          "Use Sandbox Test Dashboard to simulate payments during development"
                        ]}
                      />
                      
                      <div className="flex justify-end mt-4">
                        <Button 
                          onClick={saveSquareConfig}
                          disabled={saving.square}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          {saving.square ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                          ) : (
                            <><Save className="h-4 w-4 mr-2" /> Save Square Configuration</>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Trial Settings */}
                  <div className="space-y-4 mb-6 p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                        <Clock className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Trial Period Settings</h4>
                        <p className="text-xs text-gray-500">Configure trial durations for new clinics</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>Default Trial Duration (Days)</Label>
                        <Input 
                          type="number" 
                          value={trialSettings.default_duration_days}
                          onChange={(e) => setTrialSettings({ ...trialSettings, default_duration_days: parseInt(e.target.value) || 30 })}
                          min={1}
                          max={365}
                        />
                      </div>
                      <div>
                        <Label>Grace Period (Days)</Label>
                        <Input 
                          type="number" 
                          value={trialSettings.grace_period_days}
                          onChange={(e) => setTrialSettings({ ...trialSettings, grace_period_days: parseInt(e.target.value) || 0 })}
                          min={0}
                          max={30}
                        />
                        <p className="text-xs text-gray-500 mt-1">After trial ends</p>
                      </div>
                      <div className="flex items-center gap-2 pt-6">
                        <Switch 
                          id="auto-trial" 
                          checked={trialSettings.auto_start_trial}
                          onCheckedChange={(checked) => setTrialSettings({ ...trialSettings, auto_start_trial: checked })}
                        />
                        <Label htmlFor="auto-trial">Auto-start trial for new clinics</Label>
                      </div>
                    </div>
                    
                    <div className="flex justify-end">
                      <Button 
                        onClick={saveTrialSettings}
                        disabled={saving.trial}
                        variant="outline"
                      >
                        {saving.trial ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                        ) : (
                          <><Save className="h-4 w-4 mr-2" /> Save Trial Settings</>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Payment Settings */}
                  <div className="space-y-4 mb-6 p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
                        <DollarSign className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Payment Settings</h4>
                        <p className="text-xs text-gray-500">Currency, tax, and retry configurations</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>Currency</Label>
                        <select 
                          className="w-full px-3 py-2 border rounded-md"
                          value={paymentSettings.currency}
                          onChange={(e) => setPaymentSettings({ ...paymentSettings, currency: e.target.value })}
                        >
                          <option value="USD">USD ($)</option>
                          <option value="EUR">EUR (€)</option>
                          <option value="GBP">GBP (£)</option>
                          <option value="CAD">CAD ($)</option>
                          <option value="AUD">AUD ($)</option>
                        </select>
                      </div>
                      <div>
                        <Label>Tax Rate (%)</Label>
                        <Input 
                          type="number" 
                          step="0.01" 
                          value={paymentSettings.tax_rate}
                          onChange={(e) => setPaymentSettings({ ...paymentSettings, tax_rate: parseFloat(e.target.value) || 0 })}
                          min={0}
                          max={100}
                        />
                        <p className="text-xs text-gray-500 mt-1">Applied to all invoices</p>
                      </div>
                      <div>
                        <Label>Failed Payment Retries</Label>
                        <Input 
                          type="number" 
                          value={paymentSettings.failed_payment_retries}
                          onChange={(e) => setPaymentSettings({ ...paymentSettings, failed_payment_retries: parseInt(e.target.value) || 0 })}
                          min={0}
                          max={10}
                        />
                        <p className="text-xs text-gray-500 mt-1">Automatic retry attempts</p>
                      </div>
                    </div>
                    
                    <div className="flex justify-end">
                      <Button 
                        onClick={savePaymentSettings}
                        disabled={saving.payment}
                        variant="outline"
                      >
                        {saving.payment ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                        ) : (
                          <><Save className="h-4 w-4 mr-2" /> Save Payment Settings</>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Invoice Settings */}
                  <div className="space-y-4 p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-600 rounded-lg flex items-center justify-center">
                        <FileText className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold">Invoice Settings</h4>
                        <p className="text-xs text-gray-500">Invoice generation and terms</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>Invoice Prefix</Label>
                        <Input 
                          value={invoiceSettings.invoice_prefix}
                          onChange={(e) => setInvoiceSettings({ ...invoiceSettings, invoice_prefix: e.target.value })}
                          placeholder="INV-"
                        />
                        <p className="text-xs text-gray-500 mt-1">e.g., INV-2024-001</p>
                      </div>
                      <div>
                        <Label>Payment Terms (Days)</Label>
                        <Input 
                          type="number" 
                          value={invoiceSettings.payment_terms_days}
                          onChange={(e) => setInvoiceSettings({ ...invoiceSettings, payment_terms_days: parseInt(e.target.value) || 30 })}
                          min={1}
                          max={90}
                        />
                        <p className="text-xs text-gray-500 mt-1">Due date from invoice date</p>
                      </div>
                      <div className="flex items-center gap-2 pt-6">
                        <Switch 
                          id="auto-invoice" 
                          checked={invoiceSettings.auto_generate_invoices}
                          onCheckedChange={(checked) => setInvoiceSettings({ ...invoiceSettings, auto_generate_invoices: checked })}
                        />
                        <Label htmlFor="auto-invoice">Auto-generate on payment</Label>
                      </div>
                    </div>
                    
                    <div className="flex justify-end">
                      <Button 
                        onClick={saveInvoiceSettings}
                        disabled={saving.invoice}
                        variant="outline"
                      >
                        {saving.invoice ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                        ) : (
                          <><Save className="h-4 w-4 mr-2" /> Save Invoice Settings</>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="general" className="space-y-6 mt-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">General Settings</h3>
                <p className="text-gray-600">Platform name, branding, and basic configuration</p>
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">Settings panel coming soon. This will include platform configuration, email templates, notification settings, and more.</p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="email" className="space-y-6 mt-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Email Configuration</h3>
                <p className="text-gray-600">SMTP settings and email templates</p>
                <div className="mt-4 space-y-2 text-sm">
                  <p><strong>Current SMTP Status:</strong> <span className="text-green-600">Configured</span></p>
                  <p><strong>Templates:</strong> 5 email templates active</p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="system" className="space-y-6 mt-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">System Status</h3>
                <p className="text-gray-600">Background tasks and system health</p>
                <div className="mt-4 space-y-2 text-sm">
                  <p><strong>Scheduler:</strong> <span className="text-green-600">Running</span></p>
                  <p><strong>Automated Reminders:</strong> <span className="text-green-600">Active</span></p>
                  <p><strong>Email Service:</strong> <span className="text-green-600">Operational</span></p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
